import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronLeftIcon, LoaderIcon } from '@/components/icons';
import { toast } from 'sonner';
import { fullErrorText } from '@/api/errors';
import { sandboxClassApi } from '@/api/sandboxClass.api';
import { sandboxApi } from '@/api/sandbox.api';
import { sandboxConfigApi } from '@/api/sandboxConfig.api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { SandboxInstanceFromClassInput } from '@/api/sandboxClass.api';

function IconBox() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
      />
    </svg>
  );
}

function IconCpu() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
      />
    </svg>
  );
}

function IconGlobe() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
      />
    </svg>
  );
}

function SectionCard({
  id,
  icon,
  title,
  subtitle,
  children,
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20">
      <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">
        <div className="h-0.5 bg-gradient-to-r from-primary/80 via-primary/40 to-transparent" />
        <div className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0">
              {icon}
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">{title}</h2>
              {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
          </div>
          {children}
        </div>
      </div>
    </section>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

interface ClassParamEntry {
  name: string;
  value: string;
}

export default function CreateSandboxInstanceFromClassPage() {
  const { uid } = useParams<{ uid: string }>();
  const [searchParams] = useSearchParams();
  const instanceId = searchParams.get('instanceId');
  const navigate = useNavigate();
  const isEditMode = !!instanceId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [classParams, setClassParams] = useState<ClassParamEntry[]>([]);

  const { data: classData, isLoading: classLoading } = useQuery({
    queryKey: ['sandbox-class', uid],
    queryFn: () => sandboxClassApi.getSandboxClass(uid!),
    enabled: !!uid,
  });

  const { data: instanceData, isLoading: instanceLoading } = useQuery({
    queryKey: ['sandbox', instanceId],
    queryFn: () => sandboxApi.getSandbox(instanceId!),
    enabled: isEditMode && !!instanceId,
  });

  const createMutation = useMutation({
    mutationFn: ({
      classUid,
      data,
    }: {
      classUid: string;
      data: { name: string; description?: string; params?: Record<string, string> };
    }) =>
      sandboxClassApi.createSandboxInstanceFromClass(classUid, {
        name: data.name,
        description: data.description || '',
        params: data.params || {},
      } satisfies SandboxInstanceFromClassInput),
    onSuccess: () => navigate('/plugins/sandboxes?tab=instance'),
    onError: (error) => toast.error(`创建失败：${fullErrorText(error)}`),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      classUid,
      data,
    }: {
      id: string;
      classUid: string;
      data: { name: string; description?: string; params?: Record<string, string> };
    }) =>
      sandboxClassApi.updateSandboxInstanceFromClass(id, classUid, {
        name: data.name,
        description: data.description || '',
        params: data.params || {},
      } satisfies SandboxInstanceFromClassInput),
    onSuccess: () => navigate('/plugins/sandboxes?tab=instance'),
    onError: (error) => toast.error(`保存失败：${fullErrorText(error)}`),
  });

  useEffect(() => {
    if (!classData) return;

    const params: ClassParamEntry[] = (classData.params || []).map((p) => ({
      name: p.name,
      value: p.paramValue?.value || '',
    }));

    if (isEditMode && instanceData) {
      setName(instanceData.name || '');
      setDescription(instanceData.description || '');
      const existingParams = instanceData.classParams || {};
      params.forEach((p) => {
        if (existingParams[p.name] !== undefined) {
          p.value = existingParams[p.name];
        }
      });
    }

    setClassParams(params);
  }, [classData, instanceData, isEditMode]);

  const handleSubmit = () => {
    if (!uid || !name) return;

    const params: Record<string, string> = {};
    classParams.forEach((p) => {
      if (p.name) {
        params[p.name] = p.value;
      }
    });

    if (isEditMode && instanceId) {
      updateMutation.mutate({
        id: instanceId,
        classUid: uid,
        data: {
          name,
          description: description || undefined,
          params: Object.keys(params).length > 0 ? params : undefined,
        },
      });
    } else {
      createMutation.mutate({
        classUid: uid,
        data: {
          name,
          description: description || undefined,
          params: Object.keys(params).length > 0 ? params : undefined,
        },
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isLoading = classLoading || (isEditMode && instanceLoading);

  const template = classData?.template;
  const specId = template?.sandboxSpecId;
  const envId = template?.sandboxEnvironmentId;

  const { data: specData } = useQuery({
    queryKey: ['sandbox-spec', specId],
    queryFn: () => sandboxConfigApi.getSandboxSpec(specId!),
    enabled: !!specId,
  });

  const { data: envData } = useQuery({
    queryKey: ['sandbox-environment', envId],
    queryFn: () => sandboxConfigApi.getSandboxEnvironment(envId!),
    enabled: !!envId,
  });

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="w-full px-6 pt-8 pb-4">
        <button
          onClick={() =>
            isEditMode
              ? navigate('/plugins/sandboxes?tab=instance')
              : navigate(`/plugins/sandboxes/classes/${uid}`)
          }
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          {isEditMode ? '返回沙箱实例列表' : '返回沙箱模版详情'}
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">
              {isEditMode ? '编辑沙箱实例' : '创建沙箱实例'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isEditMode
                ? `基于沙箱模版「${classData?.name || uid}」编辑实例`
                : `从沙箱模版「${classData?.name || uid}」创建`}
            </p>
          </div>
        </div>
      </div>

      <div className="w-full px-6 pb-16">
        <div className="space-y-5">
          <SectionCard id="basic" icon={<IconBox />} title="实例信息" subtitle="填写实例基本信息">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm font-medium">
                  实例名称 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="例如: production-cache、staging-worker-01"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-10"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="desc" className="text-sm font-medium">
                  描述
                </Label>
                <textarea
                  id="desc"
                  placeholder="简要描述此沙箱实例的用途..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                />
              </div>

              {classParams.length > 0 && (
                <div className="pt-4 border-t border-border">
                  <Label className="text-sm font-medium mb-3 block">模版参数</Label>
                  <div className="space-y-2">
                    {classParams.map((param, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <Input
                          value={param.name}
                          disabled
                          className="h-9 w-40 bg-muted font-mono text-xs shrink-0"
                        />
                        <Input
                          value={param.value}
                          onChange={(e) => {
                            const updated = [...classParams];
                            updated[index].value = e.target.value;
                            setClassParams(updated);
                          }}
                          className="h-9 font-mono text-xs flex-1"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </SectionCard>

          <SectionCard
            id="resource"
            icon={<IconCpu />}
            title="资源配置"
            subtitle="来自沙箱模版的配置（只读）"
          >
            <div className="space-y-1">
              <InfoRow label="名称" value={specData?.name || specId || '-'} />
              <InfoRow label="描述" value={specData?.description || '-'} />
            </div>
          </SectionCard>

          <SectionCard
            id="env"
            icon={<IconGlobe />}
            title="环境配置"
            subtitle="来自沙箱模版的配置（只读）"
          >
            <div className="space-y-1">
              <InfoRow label="名称" value={envData?.name || envId || '-'} />
              <InfoRow label="描述" value={envData?.description || '-'} />
            </div>
          </SectionCard>

          <div className="pt-4">
            <Button
              onClick={handleSubmit}
              disabled={isPending || isLoading || !name}
              className="w-full h-11 gap-2 text-base"
            >
              {isPending ? (
                <>
                  <LoaderIcon className="h-4 w-4 animate-spin" />
                  {isEditMode ? '保存中...' : '创建中...'}
                </>
              ) : isEditMode ? (
                '保存修改'
              ) : (
                '创建实例'
              )}
            </Button>
            {!name && (
              <p className="text-xs text-muted-foreground text-center mt-2">请填写实例名称</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
